import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import STLViewer from "@/components/STLViewer";
import { Upload, ArrowRight, Info, Cpu, Weight, Ruler, Layers } from "lucide-react";
import { formatEUR } from "@/lib/format";

// Density (g/cm³) of common materials
const MATERIAL_DENSITY = {
  PLA: 1.24, PETG: 1.27, "PETG-CF": 1.30, ASA: 1.07, ABS: 1.04, TPU: 1.21,
};

// Rough production cost estimate: material cost + printing time cost + finishing
const HOURLY_RATE = 4.0; // €/h
const MARGIN = 1.5;
const MATERIAL_COST_PER_KG = { PLA: 25, PETG: 28, "PETG-CF": 42, ASA: 30, ABS: 22, TPU: 55 };
const PRINT_SPEED_G_PER_MIN = 0.4; // ~24 g/h Bambu Lab realistic

export default function STLAnalyzer() {
  const [file, setFile] = useState(null);
  const [buffer, setBuffer] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [material, setMaterial] = useState("PLA");
  const [infill, setInfill] = useState(20);
  const [qty, setQty] = useState(1);
  const inputRef = useRef(null);

  async function onFile(f) {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".stl")) {
      alert("Apenas ficheiros .stl são suportados no analisador (aceitamos outros formatos no pedido de orçamento).");
      return;
    }
    setFile(f);
    const buf = await f.arrayBuffer();
    setBuffer(buf);
  }

  const density = MATERIAL_DENSITY[material];
  const rawWeight = analysis ? analysis.volumeCm3 * density : 0;
  const infillFactor = 0.35 + (infill / 100) * 0.65; // 0% -> 0.35, 100% -> 1.0 (rough approximation for shell + walls + infill)
  const effectiveWeight = rawWeight * infillFactor;
  const printMinutes = effectiveWeight / PRINT_SPEED_G_PER_MIN;
  const materialCost = (effectiveWeight / 1000) * (MATERIAL_COST_PER_KG[material] || 25);
  const printCost = (printMinutes / 60) * HOURLY_RATE;
  const unitCost = (materialCost + printCost) * MARGIN;
  const totalPrice = unitCost * qty;

  return (
    <div className="anim-in">
      <div className="border-b border-border">
        <div className="container-x py-14">
          <p className="tech-label mb-3">Ferramenta · Analisador STL</p>
          <h1 className="text-4xl lg:text-5xl font-bold tracking-tighter max-w-4xl">
            Estimativa <span className="text-brand">instantânea</span>.
          </h1>
          <p className="mt-4 text-muted-foreground max-w-2xl">
            Carrega o teu ficheiro STL para visualizar e obter uma estimativa automática de peso, tempo de impressão e preço. Rigoroso? Não. Rápido e útil? Muito.
          </p>
          <div className="mt-4 mono text-[10px] uppercase tracking-widest inline-flex items-center gap-2 px-3 py-1.5 border border-amber-300 bg-amber-50 text-amber-800">
            <Info size={12} /> Estimativa · o orçamento oficial pode diferir após análise técnica
          </div>
        </div>
      </div>

      <div className="container-x py-12 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          {!buffer ? (
            <label onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files?.[0]); }}
                   onDragOver={(e) => e.preventDefault()}
                   className="cursor-pointer border border-dashed border-border p-16 flex flex-col items-center justify-center crosshair-corners bg-card">
              <Upload size={32} className="text-brand" />
              <p className="mt-4 font-medium">Arrasta um ficheiro STL ou clica para escolher</p>
              <p className="tech-label mt-2">apenas .stl · máx. 50 MB · processado localmente no browser</p>
              <input type="file" hidden accept=".stl" ref={inputRef} onChange={(e) => onFile(e.target.files?.[0])} data-testid="stl-file-input" />
            </label>
          ) : (
            <>
              <STLViewer arrayBuffer={buffer} onAnalysis={setAnalysis} height={440} />
              <div className="mt-3 flex items-center justify-between text-xs">
                <span className="mono text-muted-foreground uppercase">{file?.name}</span>
                <button onClick={() => { setFile(null); setBuffer(null); setAnalysis(null); }} className="link-underline text-brand" data-testid="stl-reset">Escolher outro</button>
              </div>
            </>
          )}
        </div>

        <aside className="border border-border p-6 bg-card space-y-4 h-fit">
          <p className="tech-label">Parâmetros</p>
          <div>
            <label className="tech-label block mb-1">Material</label>
            <select className="input" value={material} onChange={(e) => setMaterial(e.target.value)} data-testid="stl-material">
              {Object.keys(MATERIAL_DENSITY).map((m) => <option key={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="tech-label block mb-1">Preenchimento ({infill}%)</label>
            <input type="range" min={10} max={100} step={5} value={infill} onChange={(e) => setInfill(Number(e.target.value))} className="w-full accent-brand" data-testid="stl-infill" />
          </div>
          <div>
            <label className="tech-label block mb-1">Quantidade</label>
            <input type="number" min={1} value={qty} onChange={(e) => setQty(Math.max(1, Number(e.target.value)))} className="input" data-testid="stl-qty" />
          </div>

          {analysis && (
            <div className="border-t border-border pt-4 space-y-3">
              <p className="tech-label">Análise</p>
              <Metric icon={Ruler} k="Dimensões" v={`${analysis.bbox.x.toFixed(1)} × ${analysis.bbox.y.toFixed(1)} × ${analysis.bbox.z.toFixed(1)} mm`} />
              <Metric icon={Layers} k="Volume" v={`${analysis.volumeCm3.toFixed(2)} cm³`} />
              <Metric icon={Weight} k="Peso estimado" v={`${effectiveWeight.toFixed(1)} g`} sub={`bruto: ${rawWeight.toFixed(1)} g · ${material}`} />
              <Metric icon={Cpu} k="Tempo estimado" v={`${(printMinutes / 60).toFixed(1)} h`} sub={`~${printMinutes.toFixed(0)} min`} />
              <Metric icon={Layers} k="Triângulos" v={analysis.triangleCount.toLocaleString("pt-PT")} />

              <div className="mt-4 border-t border-border pt-4">
                <p className="tech-label">Estimativa de preço</p>
                <div className="text-4xl font-bold tracking-tighter mt-2 text-brand" data-testid="stl-price">{formatEUR(totalPrice)}</div>
                <p className="mono text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
                  {qty}× {formatEUR(unitCost)} · IVA não incluído
                </p>
              </div>

              <Link to="/pedido-orcamento" className="btn-primary w-full justify-center mt-2" data-testid="stl-request-quote">
                Pedir orçamento oficial <ArrowRight size={14} />
              </Link>
            </div>
          )}
        </aside>
      </div>
      <style>{`.input{width:100%;border:1px solid hsl(var(--border));background:hsl(var(--background));padding:.5rem .75rem;font-size:.875rem;outline:none}.input:focus{border-color:var(--brand)}`}</style>
    </div>
  );
}

const Metric = ({ icon: Icon, k, v, sub }) => (
  <div className="flex items-start gap-3">
    <Icon size={14} className="text-brand mt-0.5" />
    <div className="flex-1">
      <p className="tech-label">{k}</p>
      <p className="font-semibold text-sm">{v}</p>
      {sub && <p className="mono text-[10px] text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  </div>
);
